// ISO 14229-1 DTC status byte: decoding, plain-language verdict, consistency checks,
// status-mask request building, and a simplified lifecycle simulator. Shared with
// the UDS Diagnostic Session Simulator (lib/uds/dtc.js re-exports decodeStatusByte
// and isFailingNow).
import { DTC_STATUS_BITS, hex2 } from './constants';

const has = (status, bit) => (status & bit) !== 0;
const TF = 0x01;
const TFTOC = 0x02;
const PDTC = 0x04;
const CDTC = 0x08;
const TNCSLC = 0x10;
const TFSLC = 0x20;
const TNCTOC = 0x40;
const WIR = 0x80;

export function decodeStatusByte(status) {
  return DTC_STATUS_BITS.filter((b) => (status & b.bit) !== 0);
}

export function isFailingNow(status) {
  return (status & 0x01) !== 0 || (status & 0x08) !== 0;
}

export const statusToBinary = (status) => status.toString(2).padStart(8, '0');

// Primary verdict + extra flags. `key` is stable for tests/UI styling.
export function statusVerdict(status) {
  let primary;
  if (has(status, TF)) {
    primary = has(status, CDTC)
      ? { key: 'active', tone: 'danger', title: 'Active fault right now', detail: 'The most recent test failed (bit 0) and the fault is confirmed (bit 3).' }
      : { key: 'active', tone: 'danger', title: 'Active fault right now', detail: 'The most recent test failed (bit 0). It is not confirmed yet (bit 3 clear).' };
  } else if (has(status, CDTC)) {
    primary = has(status, TNCTOC)
      ? { key: 'stored', tone: 'warning', title: 'Stored / historic (confirmed, currently passing)', detail: 'Confirmed in memory (bit 3). The last completed test passed (bit 0 clear), but the test has not completed yet this operation cycle (bit 6), so "passing" refers to the last result.' }
      : { key: 'stored', tone: 'warning', title: 'Stored / historic (confirmed, currently passing)', detail: 'Confirmed in memory (bit 3), but the most recent test passed (bit 0 clear). The fault happened earlier and is not present now.' };
  } else if (has(status, PDTC)) {
    primary = { key: 'pending', tone: 'warning', title: 'Pending — failed once, not yet confirmed', detail: 'The test failed in this or the previous operation cycle (bit 2). If it fails again the ECU will usually confirm it; the confirmation threshold is OEM-defined.' };
  } else if (has(status, TNCSLC)) {
    primary = { key: 'unknown', tone: 'muted', title: 'Monitor hasn\'t run since clear — status unknown', detail: 'The test has not completed since DTCs were last cleared (bit 4), so the ECU cannot yet say whether the fault is present.' };
  } else if (has(status, TFSLC)) {
    primary = { key: 'healed', tone: 'ok', title: 'Failed earlier since the last clear, passing now', detail: 'The test failed at some point since the last clear (bit 5), but it is not pending or confirmed and the latest result is a pass.' };
  } else {
    primary = { key: 'ok', tone: 'ok', title: 'No fault recorded', detail: 'No failure bits are set. If bit 4 is also clear, the test has run since the last clear and passed.' };
  }
  const flags = [];
  if (has(status, WIR)) flags.push({ key: 'mil', title: 'MIL / warning lamp requested', detail: 'Bit 7: the ECU is asking for the warning indicator (e.g. the check-engine lamp) to be lit for this DTC.' });
  if (has(status, TF) && has(status, PDTC) && !has(status, CDTC)) flags.push({ key: 'pending', title: 'Also pending', detail: 'Bit 2 is set: it will be confirmed if the failure repeats (OEM-defined threshold).' });
  if (has(status, TNCTOC) && !has(status, TNCSLC)) flags.push({ key: 'notrun', title: 'Test not completed this cycle', detail: 'Bit 6: the monitor has not finished in the current operation cycle, so bit 0 may still reflect an earlier cycle.' });
  return { primary, flags };
}

// Combinations that should not occur under ISO 14229-1 status-bit rules. Real ECUs
// can still report them (unsupported bits, firmware bugs), so they are "suspicious",
// not "impossible".
// `supported` is the DTCStatusAvailabilityMask when known: a rule that relies on a
// bit the ECU does not support is skipped rather than reported.
export function statusContradictions(status, supported = 0xff) {
  const issues = [];
  const add = (key, text) => issues.push({ key, text });
  const sup = (bit) => (supported & bit) !== 0;
  if (has(status, TF) && !has(status, TFSLC) && sup(TFSLC)) add('tf-no-tfslc', 'Bit 0 (testFailed) is set but bit 5 (testFailedSinceLastClear) is clear. A failure now implies a failure since the last clear — unless this ECU does not support bit 5 (check the availability mask).');
  if (has(status, TFTOC) && !has(status, TFSLC) && sup(TFSLC)) add('tftoc-no-tfslc', 'Bit 1 (failed this operation cycle) is set but bit 5 (failed since last clear) is clear — a failure this cycle is also a failure since the last clear.');
  if (has(status, PDTC) && !has(status, TFSLC) && sup(TFSLC)) add('pdtc-no-tfslc', 'Bit 2 (pending) is set but bit 5 (failed since last clear) is clear — a DTC can only be pending after a failure.');
  if (has(status, CDTC) && !has(status, TFSLC) && sup(TFSLC)) add('cdtc-no-tfslc', 'Bit 3 (confirmed) is set but bit 5 (failed since last clear) is clear. Normally a confirmed DTC has failed since the last clear; some ECUs keep bit 3 after ageing resets bit 5, or do not support bit 5.');
  if (has(status, TFTOC) && has(status, TNCTOC)) add('tftoc-tnctoc', 'Bits 1 and 6 are both set: "failed this operation cycle" and "not completed this operation cycle" contradict each other — a failed result means the test did complete.');
  if (has(status, TNCSLC) && (has(status, TFSLC) || has(status, TF) || has(status, CDTC) || has(status, PDTC))) add('tncslc-failed', 'Bit 4 (test not completed since last clear) is set together with a failure bit (0, 2, 3 or 5). If the test failed, it completed at least once since the clear.');
  if (has(status, TF) && !has(status, TFTOC) && !has(status, TNCTOC) && sup(TFTOC) && sup(TNCTOC)) add('tf-no-tftoc', 'Bit 0 (testFailed) is set and the test has completed this cycle (bit 6 clear), but bit 1 (failed this cycle) is clear. If the latest completed result this cycle is a fail, bit 1 should also be set.');
  if (has(status, WIR) && !has(status, CDTC) && !has(status, PDTC) && !has(status, TF)) add('wir-alone', 'Bit 7 (warning indicator) is set with no active, pending or confirmed fault. That is unusual, although OEMs define exactly when the lamp turns off.');
  return issues;
}

// 0x19 0x02 request builder. ECUs report DTC n when (status_n AND mask) != 0.
export function buildStatusMaskRequest(mask) {
  return [0x19, 0x02, mask & 0xff];
}

export function explainMaskMatch(mask, status) {
  const and = mask & status;
  return { and, matches: and !== 0, text: `0x${hex2(status)} AND 0x${hex2(mask)} = 0x${hex2(and)} → ${and !== 0 ? 'reported' : 'not reported'}` };
}

// --- Simplified lifecycle simulator (ISO 14229-1 Annex D style) --------------
// One "action" = one whole operation cycle (start of cycle + the test result), or a
// 0x14 clear. Confirmation and ageing thresholds are OEM-defined; the defaults here
// are teaching values, not any particular OEM's.

export const LIFECYCLE_DEFAULTS = { confirmThreshold: 2, ageingThreshold: 3, lampOffAfter: 3 };

export function initialLifecycleState() {
  return { status: 0x50, failStreak: 0, passStreak: 0, cyclesSinceConfirmedPass: 0 };
}

export function applyLifecycleAction(state, action, settings = LIFECYCLE_DEFAULTS) {
  const { confirmThreshold, ageingThreshold, lampOffAfter } = { ...LIFECYCLE_DEFAULTS, ...settings };
  if (action === 'clear') {
    return { next: initialLifecycleState(), notes: ['0x14 ClearDiagnosticInformation: all bits reset; bits 4 and 6 set because no test has completed since the clear (0x50).'] };
  }
  let s = state.status;
  const notes = [];
  // Start of a new operation cycle.
  s &= ~TFTOC;
  s |= TNCTOC;
  let { failStreak, passStreak, cyclesSinceConfirmedPass } = state;

  if (action === 'fail') {
    s |= TF | TFTOC | PDTC | TFSLC;
    s &= ~(TNCTOC | TNCSLC);
    failStreak += 1;
    passStreak = 0;
    cyclesSinceConfirmedPass = 0;
    notes.push('Test failed: bits 0, 1, 2 and 5 set; bits 4 and 6 cleared (the test completed).');
    if (!(s & CDTC) && failStreak >= confirmThreshold) {
      s |= CDTC | WIR;
      notes.push(`Failed in ${failStreak} consecutive cycles (threshold ${confirmThreshold}): confirmed (bit 3) and warning lamp requested (bit 7).`);
    } else if (!(s & CDTC)) {
      notes.push(`Failure ${failStreak} of ${confirmThreshold} needed to confirm — pending only.`);
    }
  } else if (action === 'pass') {
    s &= ~(TF | TNCTOC | TNCSLC);
    passStreak += 1;
    failStreak = 0;
    notes.push('Test passed: bit 0 cleared; bits 4 and 6 cleared (the test completed).');
    if (s & PDTC) {
      s &= ~PDTC;
      notes.push('A whole cycle completed without failing, so pending (bit 2) is cleared.');
    }
    if ((s & WIR) && passStreak >= lampOffAfter) {
      s &= ~WIR;
      notes.push(`${lampOffAfter} consecutive passing cycles: warning lamp request (bit 7) cleared.`);
    }
    if (s & CDTC) {
      cyclesSinceConfirmedPass += 1;
      if (cyclesSinceConfirmedPass >= ageingThreshold) {
        s &= ~CDTC;
        s &= ~TFSLC;
        cyclesSinceConfirmedPass = 0;
        notes.push(`Ageing: ${ageingThreshold} fault-free cycles since the last failure — the confirmed DTC ages out (bits 3 and 5 cleared; OEMs differ on bit 5).`);
      } else {
        notes.push(`Ageing counter ${cyclesSinceConfirmedPass}/${ageingThreshold}.`);
      }
    }
  } else if (action === 'notrun') {
    failStreak = state.failStreak; // an incomplete cycle neither confirms nor heals
    notes.push('Test did not run: bit 1 cleared and bit 6 set at the start of the cycle; nothing else changes (pending is not cleared by a cycle where the test did not complete).');
  }
  return { next: { status: s & 0xff, failStreak, passStreak, cyclesSinceConfirmedPass }, notes };
}
